import { Box, Button, DataTable, type AsyncDataTableRef, type DataTableColumn, LoadError, EmptyState, NoResults } from "@dehub/theme";
import { StorefrontIcon } from "@phosphor-icons/react";
import { forwardRef, useEffect, useImperativeHandle, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BusinessApi } from "@/apis";
import type { IBusinessListRow } from "@/models/Business";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { message } from "@/utils/message";
import type { BusinessType } from "../types";
import classes from "../business.module.css";

type Props = Readonly<{
  type: BusinessType;
  query: string;
  columns: DataTableColumn<IBusinessListRow>[];
  /** Current page (1-based) and its setter: the page lives in the URL, so the table is controlled. */
  page: number;
  onPageChange: (page: number) => void;
  /** Opens the add drawer (the empty state offers it). */
  onAdd: () => void;
  /** Why adding is not possible (permission codes); null when it is. */
  addReason: ReactNode;
}>;

const PAGE_SIZE = 10;

export const BusinessTable = forwardRef<AsyncDataTableRef, Props>(
  ({ type, query, columns, page, onPageChange, onAdd, addReason }, ref) => {
  const { t } = useTranslation("business");
  const { t: tCommon } = useTranslation("common");
  const [rows, setRows] = useState<IBusinessListRow[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useImperativeHandle(ref, () => ({ reload: () => setTick((n) => n + 1) }), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (BusinessApi.list({ offset: { page, limit: PAGE_SIZE }, filter: { query, type } } as any) as Promise<{
      rows?: IBusinessListRow[];
      count?: number;
    }>)
      .then((res) => {
        if (cancelled) return;
        const found = res.rows ?? [];
        const total = res.count ?? 0;
        setRows(found);
        setCount(total);
        // A stale ?page= beyond the last page lands on the last one.
        if (found.length === 0 && total > 0 && page > 1) onPageChange(Math.max(1, Math.ceil(total / PAGE_SIZE)));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A failed request is an error with a retry, never "no businesses".
        const text = (error as { message?: string })?.message ?? tCommon("error.generic");
        setRows([]);
        setCount(0);
        setLoadError(text);
        message.error(text);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, type, tick]);

  const retry = () => setTick((n) => n + 1);

  const searching = query.length > 0;

  const emptyState = loadError ? (
    <LoadError title={t("list.loadErrorTitle")} message={loadError} retryLabel={tCommon("actions.retry")} onRetry={retry} />
  ) : (
    searching ? (
      <NoResults query={query} title={t("list.noResultsTitle")} description={t("list.noResultsText", { query })} />
    ) : (
      <EmptyState
        icon={<StorefrontIcon size={24} aria-hidden="true" />}
        title={t(`list.emptyTitle.${type}`)}
        description={t("list.emptyText")}
        action={
          <ReasonTooltip reason={addReason}>
            <Button variant="default" onClick={onAdd}>
              {t(`addBtn.${type}`)}
            </Button>
          </ReasonTooltip>
        }
      />
    )
  );

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = (page - 1) * PAGE_SIZE + 1;

  return (
    <Box className={classes.tableWrap}>
      <DataTable<IBusinessListRow>
        data={rows}
        rowKey={(r) => r.id}
        cardPadding={0}
        columns={columns}
        loading={loading}
        emptyState={emptyState}
        pagination={{
          page,
          total: totalPages,
          onChange: onPageChange,
          label: count > 0 ? `${from}-${Math.min(page * PAGE_SIZE, count)} / ${count} ${t("list.records")}` : `0 ${t("list.records")}`,
        }}
        startIndex={(page - 1) * PAGE_SIZE}
        tableProps={{ striped: false, highlightOnHover: true, verticalSpacing: "sm", stickyHeader: false }}
      />
    </Box>
  );
});

BusinessTable.displayName = "BusinessTable";
